import * as THREE from "three";

export class ObstacleManager {
  constructor(scene, blockManager) {
    this.scene = scene;
    this.blockManager = blockManager;
    this.obstacles = [];

    // Obstacle dimensions are scaled to work with our frustumHeight of 10
    // 0.5 units = 50px at 1000px effective height
    this.obstacleSize = 0.5; // 50px at 1000px effective height

    // Define spike obstacle colors
    this.obstacleTypes = [
      { color: 0x000000 }, // Black
    ];

    // Shared geometry + material reused by every spike. All spikes are identical
    // in shape/size (only position differs), so one geometry + one material is
    // reused instead of allocating a BufferGeometry + material per spike.
    // Disposed once in dispose(); collision uses per-mesh `bounds`, unaffected.
    this.sharedGeometry = this.buildSpikeGeometry();
    this.sharedMaterial = new THREE.MeshStandardMaterial({
      color: this.obstacleTypes[0].color,
      roughness: 0.7,
      metalness: 0.2,
      emissive: this.obstacleTypes[0].color,
      emissiveIntensity: 1.0,
      side: THREE.DoubleSide, // Render both sides
    });
  }

  // Build the shared triangular spike geometry: a 0.5-wide, 0.5-tall triangle
  // with front and back faces (so it's visible from both sides).
  buildSpikeGeometry() {
    const geometry = new THREE.BufferGeometry();

    const vertices = new Float32Array([
      // Triangle face - front side
      -0.25, 0.0, 0.025, // bottom left
      0.25, 0.0, 0.025, // bottom right
      0.0, 0.5, 0.025, // top center

      // Triangle face - back side (for visibility from behind)
      -0.25, 0.0, -0.025, // bottom left
      0.25, 0.0, -0.025, // bottom right
      0.0, 0.5, -0.025, // top center
    ]);

    const indices = [
      0, 1, 2, // front face
      5, 4, 3, // back face (reversed order for correct normal)
    ];

    geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return geometry;
  }

  reset() {
    // Dispose of all obstacles properly before clearing array
    this.obstacles.forEach((obstacle) => {
      this.disposeObstacle(obstacle);
    });

    // Clear obstacles array
    this.obstacles = [];
  }

  // Create a spike at specific coordinates
  createSpike(x, y) {
    // Reuse the shared triangular geometry + material; only position differs.
    const mesh = new THREE.Mesh(this.sharedGeometry, this.sharedMaterial);
    mesh.castShadow = true;
    mesh.position.set(x + 0.25, y, 0);
    mesh.receiveShadow = false; // Don't receive shadows

    this.scene.add(mesh);

    // Store bounds in the mesh's userData for collision detection
    // Bounds match the actual triangle shape
    const bounds = {
      left: x, // Left edge of the grid cell
      right: x + 0.5, // Right edge of the grid cell (0.5 units = 50px width)
      top: y + 0.5, // Top point (top center vertex)
      bottom: y, // Bottom edge (on the ground)
    };

    // Mark this as a spike obstacle for special collision detection
    mesh.userData = {
      bounds,
      isSpike: true, // Add flag to identify as spike for special collision detection
    };

    // Add to obstacles array with AABB bounds for collision
    this.obstacles.push({
      mesh: mesh,
      type: 0, // Black spike type
      bounds: bounds,
    });

    return {
      mesh: mesh,
      bounds: bounds,
    };
  }

  // Get obstacles for collision detection
  getObstacles() {
    return this.obstacles.map((obstacle) => obstacle.mesh);
  }

  // Get obstacle bounds for collision detection
  getObstacleBounds() {
    return this.obstacles.map((obstacle) => obstacle.bounds);
  }

  // Internal helper to remove a spike's mesh from the scene. Geometry and
  // material are shared across all spikes, so they are NOT disposed here — only
  // once in dispose() at teardown.
  disposeObstacle(obstacleData) {
    if (obstacleData && obstacleData.mesh) {
      this.scene.remove(obstacleData.mesh);
      obstacleData.mesh = null; // Clear reference
    }
  }

  // Remove obstacles whose right edge is before a given x-coordinate
  removeObstaclesBeforeX(x) {
    // Iterate backwards to safely remove elements
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      const obstacle = this.obstacles[i];
      // Use obstacle bounds for position check
      if (obstacle.bounds.right < x) {
        this.disposeObstacle(obstacle);
        this.obstacles.splice(i, 1);
      }
    }
  }

  // Dispose shared GPU resources. Call once when the manager is torn down.
  dispose() {
    this.reset();
    this.sharedGeometry.dispose();
    this.sharedMaterial.dispose();
  }
}
